/**
 * OAuth Popup Manager
 * 
 * Handles OAuth flows in popup windows with postMessage communication
 * Keeps user in the parent page (wizard/settings) while authenticating
 * 
 * @module OAuthPopupManager
 */

"use client";

/**
 * OAuthPopupManager Class
 * Manages popup window for OAuth authentication
 */
export class OAuthPopupManager {
  constructor(options = {}) {
    this.popupWindow = null;
    this.popupTimer = null;
    this.messageListener = null;
    this.onSuccess = options.onSuccess || (() => {});
    this.onError = options.onError || (() => {});
    this.onClose = options.onClose || (() => {});
  }

  /**
   * Open OAuth popup
   * @param {string} url - OAuth URL to open
   * @param {string} title - Popup window title
   * @returns {Promise} Resolves when auth completes
   */
  openPopup(url, title = 'OAuth Authorization') {
    return new Promise((resolve, reject) => {
      // Popup dimensions
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      // Popup features
      const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no`;

      console.log('[OAuthPopup] Opening popup:', { url, title, features });

      // Open popup
      this.popupWindow = window.open(url, title, features);

      if (!this.popupWindow) {
        const error = 'Popup blocked. Please allow popups for this site.';
        console.error('[OAuthPopup]', error);
        this.onError({ error, message: error });
        reject(new Error(error));
        return;
      }

      // Focus popup
      try {
        this.popupWindow.focus();
      } catch (e) {
        console.warn('[OAuthPopup] Could not focus popup:', e);
      }

      // Listen for messages from popup
      this.messageListener = (event) => {
        // Validate origin
        const validOrigins = [
          window.location.origin,
          process.env.NEXT_PUBLIC_APP_URL,
        ].filter(Boolean);

        const isValidOrigin = validOrigins.some(origin => 
          event.origin === origin || event.origin.startsWith(origin)
        );

        if (!isValidOrigin && event.origin !== '*') {
          console.warn('[OAuthPopup] Invalid origin:', event.origin);
          return;
        }

        console.log('[OAuthPopup] Received message:', event.data);

        // Handle different message types
        if (event.data.type === 'oauth-success') {
          console.log('[OAuthPopup] Authentication successful');
          this.cleanup();
          this.onSuccess(event.data);
          resolve(event.data);
        } else if (event.data.type === 'oauth-error') {
          console.error('[OAuthPopup] Authentication failed:', event.data);
          this.cleanup();
          this.onError(event.data);
          reject(new Error(event.data.error || 'OAuth failed'));
        } else if (event.data.type === 'oauth-close') {
          console.log('[OAuthPopup] User closed popup');
          this.cleanup();
          resolve({ cancelled: true });
        }
      };

      window.addEventListener('message', this.messageListener);

      // Poll for popup close
      this.popupTimer = setInterval(() => {
        if (!this.popupWindow || this.popupWindow.closed) {
          console.log('[OAuthPopup] Popup closed by user');
          this.cleanup();
          this.onClose();
          resolve({ cancelled: true });
        }
      }, 500);
    });
  }

  /**
   * Close popup window
   */
  closePopup() {
    if (this.popupWindow && !this.popupWindow.closed) {
      try {
        this.popupWindow.close();
      } catch (e) {
        console.warn('[OAuthPopup] Error closing popup:', e);
      }
    }
    this.popupWindow = null;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.popupTimer) {
      clearInterval(this.popupTimer);
      this.popupTimer = null;
    }
    
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    
    this.closePopup();
  }

  /**
   * Destroy manager
   */
  destroy() {
    this.cleanup();
  }
}

export default OAuthPopupManager;





